import React, {useCallback, useEffect} from 'react';
import FormField, {Props} from '@src/components/Form/FormField';
import {IOperationModel} from '@src/@interfaces/models/Operation/IOperationModel';
import {IClientModel} from '@src/@interfaces/models/Client/IClientModel';
import {useFieldArray, useFormContext, useWatch} from 'react-hook-form';
import {ITechOperationModel} from '@src/@interfaces/models/TechOperation/ITechOperationModel';
import TechOperationOperationsField from '@views/tech-operations/fields/TechOperationOperationsField';
import TechOperationMaterialsField from '@views/tech-operations/fields/TechOperationMaterialsField';
import TechOperationMaterialCategoriesField from '@views/tech-operations/fields/TechOperationMaterialCategoriesField';
import TechOperationItemTitleField from '@views/tech-operations/fields/TechOperationItemTitleField';
import RenderIf from '@src/components/RenderIf/RenderIf';
import TechOperationItemDescriptionField from '@views/tech-operations/fields/TechOperationItemDescriptionField';

const types = {
    DEFAULT: 'default',
    RADIO: 'radio-list',
    CHECKBOX: 'checkbox',
};

const itemStyles = {
    minWidth: 250,
    position: 'relative',
} as React.CSSProperties;
const itemDeleteButtonStyles = {
    position: 'absolute',
    top: 0,
    right: 0,
    marginTop: -10,
    marginRight: -10,
} as React.CSSProperties;

function ActualForm(props: Props<IOperationModel>) {
    const {control} = useFormContext<IClientModel>();
    const {fields, replace, append, remove} = useFieldArray({
        control,
        name: props.fieldName as 'items',
        keyName: 'autoGeneratedUuid',
    });

    const currentType = useWatch({control, name: 'type'});

    const onAddNewOne = useCallback(() => {
        append({
            'id': null,
            'title': null,
            'operations': [],
            'materials': [],
            'material_categories': [],
        });
    }, [append]);

    useEffect(() => {
        if (
                [types.DEFAULT, types.CHECKBOX].includes(currentType) && fields.length < 1 ||
                [types.RADIO].includes(currentType) && fields.length < 2
        ) {
            onAddNewOne();
        } else if ([types.DEFAULT, types.CHECKBOX].includes(currentType) && fields.length > 1) {
            replace([fields[0]]);
        }
    }, [onAddNewOne, currentType, fields, replace]);

    return <div>
        <RenderIf expression={currentType === types.RADIO}>
            <div className="mb-2">
                <button type="button" className="btn btn-sm btn-outline-info" onClick={onAddNewOne}>
                    <i className="fas fa-plus"/>
                </button>
            </div>
        </RenderIf>

        <div className="row">
            {fields.map((item, index) => {
                return <div className="col" key={item.autoGeneratedUuid}>
                    <div className="p-2 pb-0 mb-3 border rounded-2" style={itemStyles}>
                        <RenderIf expression={currentType === types.RADIO && index > 1}>
                            <button
                                    type="button"
                                    className="btn btn-sm btn-warning"
                                    style={itemDeleteButtonStyles}
                                    onClick={() => {
                                        remove(index);
                                    }}
                            >
                                <i className="fas fa-times"/>
                            </button>
                        </RenderIf>

                        <RenderIf expression={[types.RADIO, types.CHECKBOX].includes(currentType)}>
                            <TechOperationItemTitleField name={`items.${index}.title`}/>
                        </RenderIf>

                        <TechOperationItemDescriptionField name={`items.${index}.description`}/>

                        <TechOperationOperationsField name={`items.${index}.operations`}/>
                        <TechOperationMaterialsField name={`items.${index}.materials`}/>
                        <TechOperationMaterialCategoriesField name={`items.${index}.material_categories`}/>
                    </div>
                </div>;
            })}
        </div>
    </div>;
}

export default function TechOperationItemsField<Model extends ITechOperationModel = ITechOperationModel>(props: Props<Model>) {
    return <FormField {...props} withoutLayout={true} fieldElement={ActualForm as any}/>;
}
